/**
 * The real implementation of the mock-api.ts endpoint contract
 * (HANDOFF.md §2: nineteen exports, environmentOf plus eighteen Promises).
 * lib/api.ts is the seam and points here unconditionally; VITE_API_MODE=mock
 * is handled INSIDE this file instead (see `useMock` below), so that every
 * export always resolves to exactly one implementation: two `export *`
 * statements in api.ts would make any name present in both files ambiguous
 * and silently unexported.
 *
 * Coverage, verified against apps/cloud/src/routes at wiring time: not
 * every family has a live route yet, so the ones that don't are re-exported
 * from the mock, unchanged, rather than faked:
 *
 *   real   getAccount (plan and its expiry from /auth/me),
 *          listProjects, getProject, listDeploys, getDeploy, listSecrets,
 *          setSecret, deleteSecret, getTraffic, listTables, listRows,
 *          insertRow, updateRow, deleteRow, listFiles, listInstallations,
 *          getInstallConnectUrl, getInstallation, checkSubdomain,
 *          connectProject, getRun, deleteProject, rollbackDeploy,
 *          renameProject (PATCH /projects/:id, name only, 2026-09-05: there
 *          is deliberately no web-address counterpart),
 *          listActivity (GET /:id/activity's raw audit trail, action strings
 *          mapped to a kind+title here, merged with real deploys), listErrors
 *          (GET /:id/obs/errors's ungrouped forensic rows, grouped
 *          client-side by route_pattern+status; no persisted new/seen/
 *          resolved state exists server-side, so every group reads 'new' -
 *          see groupErrors below), getUsage (F14: compute units are folded
 *          from an exact, unsampled request counter and its CPU time,
 *          database/file storage are measured once a day
 *          and a resource with no measurement yet is omitted rather than
 *          shown as zero; see the wire type below)
 *   mock   setDetailLevel (no PATCH /auth/me yet), listAddOns, getClaim,
 *          listTimelines, startRun (no screen calls this today: see below)
 *
 * `listRows` has a sibling, `listRowsPage`, that keeps the wire's own
 * `truncated` flag. The grid is happy with a capped page; export is not, and
 * was silently writing 200-row files for 5,000-row tables. See its own note.
 *
 * `getUsage`'s wire shapes are hand-written here rather than promoted to
 * @sv/cloud-contracts: cloud-web is deliberately OUTSIDE the pnpm workspace
 * (its own lockfile, see pnpm-workspace.yaml's comment), so a
 * `workspace:*` dependency would not resolve here, and cloud-contracts'
 * charter is "what lib/serialize.ts's functions return" while this payload
 * is built by a service module instead. A second client (cloud-cli) would
 * be the moment to promote these shapes; not yet.
 *
 * `startRun` stays mock for a different reason than the 501 stubs above: no
 * screen in this prototype actually calls it (every write action goes
 * through a local store method instead: see mock/mock-api.ts §2.3's own
 * note that most writes "do not exist in the mock" as functions at all).
 * There is nothing here to wire until those write endpoints
 * (HANDOFF.md §2.4 item 3) exist and the screens are updated to call them.
 *
 * The installations/connect/run functions below have no mock counterpart at
 * all, the demo apparatus's onboarding flow (lib/mock/onboarding.ts,
 * lib/mock/simulate.ts) is a wholly separate client-side simulation, not a
 * fake HTTP layer for these. Screens that use them (NameApp, ConnectCode,
 * FirstBuild) branch on VITE_API_MODE themselves rather than delegating the
 * choice here, see those files.
 *
 * God View (screens/admin/GodView.tsx) and the invite claim screen
 * (screens/auth/Claim.tsx) join that same list of seam exceptions: they
 * call apiFetch directly rather than getting a mock/real pair here. God
 * View has no demo value (the nav entry it's reached from is hidden
 * entirely in mock mode, `Account.isAdmin` is always `false` there, see
 * lib/mock/seed.ts's `account()`), so a mock twin would be dead code with
 * nothing exercising it.
 */
import { clearSession } from './auth/session';
import { apiFetch, ApiError } from './http';
import { t } from './i18n/t';
import * as mock from './mock/mock-api';
import {
  setDetailLevel,
  listAddOns,
  getClaim,
  listTimelines,
  startRun,
  environmentOf,
} from './mock/mock-api';
import type {
  Account,
  AccountLanguage,
  ActivityEvent,
  ActivityKind,
  Deploy,
  DeployMode,
  DeployStatus,
  DbTable,
  DbRow,
  ErrorGroup,
  FileObj,
  GithubLink,
  Health,
  PauseReason,
  Project,
  ProjectStatus,
  RepoCandidate,
  Secret,
  Traffic,
  TrafficWindow,
  Usage,
  UsageMetric,
} from './types';

export { environmentOf, setDetailLevel, listAddOns, getClaim, listTimelines, startRun };

// VITE_API_MODE=mock keeps the whole surface (including the seven functions
// below that otherwise hit cloud-api) on the demo apparatus: the demo dock,
// the scenarios, and every deep-linked ?state= screen depend on the mock's
// clock and seeded world, not on a real account existing. Checked once, not
// per call, since it never changes within a page load.
const useMock = import.meta.env.VITE_API_MODE === 'mock';

/* --------------------------------------------------------------- account */

interface AccountWire {
  id: string;
  email: string;
  display_name: string | null;
  mode: 'simple' | 'advanced';
  language?: AccountLanguage | null;
  is_admin: boolean;
  analytics_eligible?: boolean;
  cli_connected?: boolean;
  plan_id: Account['plan'];
  plan_expires_at: string | null;
  created_at: string;
  features?: { tenant_staging?: boolean; teams?: boolean; svgit?: boolean };
}

export async function getAccount(): Promise<Account> {
  if (useMock) return mock.getAccount();
  const [me, installations] = await Promise.all([
    apiFetch<AccountWire>('/api/v1/auth/me'),
    // Not exposed by /auth/me: the connected GitHub login lives on the
    // installation record, not the account. First bound installation is a
    // reasonable stand-in until there's a real single-account rollup, this
    // prototype only ever offers one "Connect GitHub" action per account.
    listInstallations().catch(() => []),
  ]);
  return {
    id: me.id,
    name: me.display_name ?? me.email.split('@')[0] ?? me.email,
    email: me.email,
    plan: me.plan_id,
    planExpiresAt: me.plan_expires_at,
    detailLevel: me.mode,
    createdAt: me.created_at,
    githubHandle: installations[0]?.githubAccountLogin ?? '',
    isAdmin: me.is_admin,
    // Older cloud-api builds do not send it; treat that as ineligible rather than guess.
    analyticsEligible: me.analytics_eligible ?? false,
    cliConnected: me.cli_connected,
    tenantStagingEnabled: me.features?.tenant_staging ?? false,
    teamsEnabled: me.features?.teams ?? false,
    svgitEnabled: me.features?.svgit ?? false,
    language: me.language ?? null,
  };
}

/**
 * Save the dashboard language on the account, so it follows the person to
 * any browser they sign in on. Mock mode has no account to save to; the
 * browser's stored prefs are the whole record there.
 */
export async function saveAccountLanguage(language: AccountLanguage): Promise<void> {
  if (useMock) return;
  await apiFetch<AccountWire>('/api/v1/auth/me', { method: 'PATCH', body: { language } });
}

/**
 * Auto-pay (2026-10-13): whether credits pay the plan at each cycle start.
 * Off means credits pay overage only and the plan ends with its cycle. Mock
 * mode has nothing to save it to.
 */
export async function saveCreditsPayPlan(on: boolean): Promise<void> {
  if (useMock) return;
  await apiFetch<AccountWire>('/api/v1/auth/me', { method: 'PATCH', body: { credits_pay_plan: on } });
}

/**
 * Delete the signed-in account (cloud-api DELETE /auth/me). `confirm` is the
 * account email, typed by the person. Refused with ACCOUNT_HAS_PROJECTS while
 * any project still exists. Clears the local session on success.
 */
export async function deleteAccount(confirm: string): Promise<void> {
  if (useMock) {
    clearSession();
    return;
  }
  await apiFetch<void>('/api/v1/auth/me', { method: 'DELETE', body: { confirm } });
  clearSession();
}

export async function signOut(): Promise<void> {
  if (!useMock) {
    try {
      await apiFetch<void>('/api/v1/auth/signout', { method: 'POST' });
    } catch {
      // Best-effort: even if network fails or returns 401, clear local session
    }
  }
  clearSession();
}

/* -------------------------------------------------------------- projects */

interface ProjectAdvancedWire {
  worker_name: string | null;
  subdomain: string;
  github_repo: string | null;
  default_branch: string;
  bundle_state: string;
  database_name: string | null;
  bucket_name: string | null;
  region: string | null;
  topology: string;
}

interface ActiveRunWire {
  id: string;
  kind: string;
  status: string;
}

interface ProjectWire {
  id: string;
  name: string;
  slug: string;
  web_address: string | null;
  status: string;
  latest_deploy_status: string | null;
  needs_initialization: boolean;
  /** An unsettled provision/teardown run, or null. See mapProjectStatus. */
  active_run?: ActiveRunWire | null;
  /** 'staged' when pushes go to staging and the live app waits for a prod tag. */
  deploy_mode?: 'direct' | 'staged';
  environment?: 'production' | 'staging';
  /** This app's staging environment, or null when it has none. */
  staging?: StagingWire | null;
  /** What GitHub last told cloud-api about the repo. Never deletes anything. */
  github_link?: GithubLink;
  /** Teams: 'collaborator' on an app shared with this account. Absent from an older cloud-api. */
  role?: 'owner' | 'collaborator';
  /** Set while the app is paused. Absent from an older cloud-api. */
  paused?: { reason: PauseReason; since: string | null; until: string | null } | null;
  /** Page caching. Absent from an older cloud-api. */
  page_cache_enabled?: boolean;
  created_at: string;
  updated_at: string;
  advanced?: ProjectAdvancedWire;
}

interface StagingWire {
  id: string;
  slug: string;
  web_address: string | null;
  status: string;
  latest_deploy_status: string | null;
}

function mapProjectStatus(wire: ProjectWire): ProjectStatus {
  // "deleted" | "orphaned" | "expired" all read as the closest existing
  // lamp state: none of them has a dedicated pill in this prototype.
  if (wire.status !== 'active') return 'down';
  // Ahead of every other rule. A project whose connect run has not settled
  // has no database, no storage and no web address yet, and this used to
  // fall through to 'ready', so somebody who navigated away mid-connect and
  // came back was told their app was finished while it was still being built.
  if (wire.active_run?.kind === 'provision') return 'provisioning';
  if (wire.active_run?.kind === 'teardown') return 'cleaning-up';
  // Paused outranks every deploy state: visitors get the holding page whatever
  // the last deploy did.
  if (wire.paused) return 'paused';
  if (wire.needs_initialization) return 'ready';
  switch (wire.latest_deploy_status) {
    case 'success':
      return 'live';
    case 'pending':
      return 'deploying';
    case 'failed':
      return 'didnt-work';
    default:
      // Connected, initialized, but no deploy row yet.
      return 'ready';
  }
}

interface SummaryWire {
  requests: number;
  errors: { '4xx': number; '5xx': number };
}

/**
 * The three numbers Health needs, for the last hour. Uses `errors['5xx']`
 * only, not 4xx: a 404 is often the visitor's mistake, not the app's, and
 * this matches the backend's own rollup rule (rollup-rule.ts), which also
 * keys its red/yellow thresholds off 5xx alone. `downMinutes` stays 0: no
 * per-minute downtime duration is tracked anywhere, frontend or backend.
 *
 * Fetched on its own, not as part of a Project: the summary is an Analytics
 * Engine query that can take seconds, and loading it inside getProject made
 * every screen (and every row of listProjects) wait for it. Only the
 * overview's traffic light reads it, so only the overview asks.
 *
 * Failure here must never break the overview, same "the page, not the app"
 * principle the traffic screen's own error state uses. A failed
 * observability query reads as "no traffic yet", not a broken page.
 */
export async function getProjectHealth(projectId: string): Promise<Health> {
  if (useMock) return mock.getProjectHealth(projectId);
  try {
    const summary = await apiFetch<SummaryWire>(
      `/api/v1/projects/${projectId}/obs/summary?range=1h`,
    );
    return { downMinutes: 0, requestsLastHour: summary.requests, errorsLastHour: summary.errors['5xx'] };
  } catch {
    return { downMinutes: 0, requestsLastHour: 0, errorsLastHour: 0 };
  }
}

async function toProject(wire: ProjectWire): Promise<Project> {
  const currentDeployId = await latestDeployId(wire.id);
  const repoFull = wire.advanced?.github_repo ?? null;
  const [owner, repoName] = repoFull ? repoFull.split('/') : [undefined, undefined];

  return {
    id: wire.id,
    slug: wire.slug,
    name: wire.name,
    status: mapProjectStatus(wire),
    createdAt: wire.created_at ?? new Date().toISOString(),
    repo:
      repoFull && owner && repoName
        ? {
            provider: 'github',
            owner,
            name: repoName,
            branch: wire.advanced?.default_branch ?? 'main',
            connected: true,
          }
        : null,
    deployMode: wire.deploy_mode ?? 'direct',
    environment: wire.environment ?? 'production',
    environments: {
      production: {
        id: wire.id,
        name: 'production',
        webAddress: wire.web_address ?? '',
        currentDeployId,
      },
      // Absent unless the owner asked for one. Its currentDeployId is left
      // null rather than fetched: this mapper already costs one request per
      // project, and the staging environment's own page loads it by slug like
      // any other project when somebody actually opens it.
      ...(wire.staging
        ? {
            staging: {
              id: wire.staging.id,
              name: 'staging' as const,
              webAddress: wire.staging.web_address ?? '',
              currentDeployId: null,
            },
          }
        : {}),
    },
    claimToken: null,
    activeRunId: wire.active_run?.id ?? null,
    githubLink: wire.github_link ?? 'ok',
    role: wire.role ?? 'owner',
    paused: wire.paused ? { reason: wire.paused.reason, until: wire.paused.until } : null,
    pageCache: wire.page_cache_enabled ?? false,
    region: wire.advanced?.region ?? 'Singapore',
    databaseName: wire.advanced?.database_name ?? '',
    fileStoreName: wire.advanced?.bucket_name ?? '',
  };
}

export async function listProjects(): Promise<Project[]> {
  if (useMock) return mock.listProjects();
  const { projects } = await apiFetch<{ projects: ProjectWire[] }>('/api/v1/projects?advanced=1');
  return Promise.all(projects.map(toProject));
}

export async function getProject(slug: string): Promise<Project | null> {
  if (useMock) return mock.getProject(slug);
  const { projects } = await apiFetch<{ projects: ProjectWire[] }>(
    `/api/v1/projects?advanced=1&slug=${encodeURIComponent(slug)}`,
  );
  const wire = projects[0];
  return wire ? toProject(wire) : null;
}

/**
 * Rename. The display name only, and there is deliberately no counterpart
 * for the web address: cloud-api has no route that changes a subdomain,
 * because it is attached to a live custom domain and baked into the
 * project's own rendered starter at connect. The Settings screen renders
 * the address as read-only text for exactly that reason.
 */
export async function renameProject(projectId: string, name: string): Promise<Project> {
  const wire = await apiFetch<ProjectWire>(`/api/v1/projects/${projectId}?advanced=1`, {
    method: 'PATCH',
    body: { name },
  });
  return toProject(wire);
}

/**
 * Pause and resume (F22). Pausing puts a holding page in front of the app and
 * changes nothing else; resuming brings the app's own latest version back with
 * no rebuild. Resume is refused (PROJECT_PAUSED) for a 'quota' pause, which
 * only credits, a plan change or the cycle reset lift.
 */
export async function pauseProject(projectId: string): Promise<Project> {
  const wire = await apiFetch<ProjectWire>(`/api/v1/projects/${projectId}/pause?advanced=1`, {
    method: 'POST',
  });
  return toProject(wire);
}

export async function resumeProject(projectId: string): Promise<Project> {
  const wire = await apiFetch<ProjectWire>(`/api/v1/projects/${projectId}/resume?advanced=1`, {
    method: 'POST',
  });
  return toProject(wire);
}

/**
 * Page caching. Applies to the running app at once with no push; turning it on
 * is refused (PROJECT_PAUSED) while the app is paused.
 */
export async function setPageCache(projectId: string, enabled: boolean): Promise<Project> {
  const wire = await apiFetch<ProjectWire>(`/api/v1/projects/${projectId}/page-cache?advanced=1`, {
    method: 'PUT',
    body: { enabled },
  });
  return toProject(wire);
}

/* --------------------------------------------------------- custom domains */

/**
 * Custom domains (F6 V1.5): an owner's own domain attached to a project. The
 * the DNS records they add live under `cname`/`txtRecords`; `status`/`sslStatus` are
 * Cloudflare's own state, surfaced so the section can name where a stuck domain
 * is stuck. Real-only, like staging: the mock returns an empty list so the
 * section renders, and the write actions say they are not part of the demo.
 */
export interface CustomDomainRecord {
  name: string;
  value: string;
}

export interface CustomDomain {
  id: string;
  hostname: string;
  status: string;
  sslStatus: string | null;
  verified: boolean;
  error: string | null;
  cname: CustomDomainRecord;
  /**
   * Every DV-certificate TXT record Cloudflare asked for. A LIST, because
   * Cloudflare returns one token per name on the certificate and showing only
   * the first leaves the owner adding one of two required records, with a
   * certificate that never issues and nothing on screen to explain it. Empty
   * until Cloudflare has minted them.
   */
  txtRecords: CustomDomainRecord[];
}

interface CustomDomainWire {
  id: string;
  hostname: string;
  status: string;
  ssl_status: string | null;
  verified: boolean;
  error: string | null;
  dns_records: {
    cname: CustomDomainRecord;
    txt_records: CustomDomainRecord[];
  };
}

const MOCK_DOMAINS_UNAVAILABLE = 'Adding a custom domain is not part of this demo.';

function toCustomDomain(wire: CustomDomainWire): CustomDomain {
  return {
    id: wire.id,
    hostname: wire.hostname,
    status: wire.status,
    sslStatus: wire.ssl_status,
    verified: wire.verified,
    error: wire.error,
    cname: wire.dns_records.cname,
    txtRecords: wire.dns_records.txt_records ?? [],
  };
}

export interface CustomDomainList {
  domains: CustomDomain[];
  /** How many custom domains this app may hold (server's CUSTOM_DOMAINS_PER_PROJECT). */
  limit: number;
}

export async function listCustomDomains(projectId: string): Promise<CustomDomainList> {
  if (useMock) return { domains: [], limit: 1 };
  const { domains, limit } = await apiFetch<{ domains: CustomDomainWire[]; limit?: number }>(
    `/api/v1/projects/${projectId}/domains`,
  );
  // An older cloud-api sends no limit; 1 was its cap.
  return { domains: domains.map(toCustomDomain), limit: limit ?? 1 };
}

export async function addCustomDomain(projectId: string, hostname: string): Promise<CustomDomain> {
  if (useMock) throw new Error(MOCK_DOMAINS_UNAVAILABLE);
  const { domain } = await apiFetch<{ domain: CustomDomainWire }>(
    `/api/v1/projects/${projectId}/domains`,
    { method: 'POST', body: { hostname } },
  );
  return toCustomDomain(domain);
}

export async function verifyCustomDomain(
  projectId: string,
  domainId: string,
): Promise<CustomDomain> {
  if (useMock) throw new Error(MOCK_DOMAINS_UNAVAILABLE);
  const { domain } = await apiFetch<{ domain: CustomDomainWire }>(
    `/api/v1/projects/${projectId}/domains/${domainId}/verify`,
    { method: 'POST' },
  );
  return toCustomDomain(domain);
}

export async function removeCustomDomain(projectId: string, domainId: string): Promise<void> {
  if (useMock) throw new Error(MOCK_DOMAINS_UNAVAILABLE);
  await apiFetch<unknown>(`/api/v1/projects/${projectId}/domains/${domainId}`, {
    method: 'DELETE',
  });
}

/**
 * Teams: who can work on a project (cloud-api's routes/members.ts). A
 * collaborator can do everything the owner can except delete the app, from
 * here, the CLI, or their agent. Real-only, like custom domains: the mock
 * returns the demo account as the only owner so the section renders, and the
 * write actions say they are not part of the demo.
 */
export interface TeamMember {
  accountId: string;
  email: string;
  displayName: string | null;
  role: 'owner' | 'collaborator';
}

export interface TeamInvitation {
  id: string;
  email: string;
  invitedBy: string | null;
  expiresAt: string;
}

export interface Team {
  members: TeamMember[];
  invitations: TeamInvitation[];
}

interface TeamWire {
  members: { account_id: string; email: string; display_name: string | null; role: TeamMember['role'] }[];
  invitations: { id: string; email: string; invited_by: string | null; expires_at: string }[];
}

export interface InviteResult {
  inviteUrl: string;
  /** False when nobody has an SV Cloud account with that email yet. */
  accountExists: boolean;
  emailSent: boolean;
}

/** An invitation addressed to the signed-in person. */
export interface MyInvitation {
  id: string;
  projectName: string;
  projectSlug: string;
  invitedBy: string | null;
  expiresAt: string;
}

interface MyInvitationWire {
  id: string;
  project_name: string;
  project_slug: string;
  invited_by: string | null;
  expires_at: string;
}

const MOCK_TEAM_UNAVAILABLE = 'Inviting people is not part of this demo.';

function toMyInvitation(w: MyInvitationWire): MyInvitation {
  return {
    id: w.id,
    projectName: w.project_name,
    projectSlug: w.project_slug,
    invitedBy: w.invited_by,
    expiresAt: w.expires_at,
  };
}

export async function getTeam(projectId: string): Promise<Team> {
  if (useMock) {
    const me = await mock.getAccount();
    return {
      members: [{ accountId: me.id, email: me.email, displayName: me.name, role: 'owner' }],
      invitations: [],
    };
  }
  const wire = await apiFetch<TeamWire>(`/api/v1/projects/${projectId}/members`);
  return {
    members: wire.members.map((m) => ({
      accountId: m.account_id,
      email: m.email,
      displayName: m.display_name,
      role: m.role,
    })),
    invitations: wire.invitations.map((i) => ({
      id: i.id,
      email: i.email,
      invitedBy: i.invited_by,
      expiresAt: i.expires_at,
    })),
  };
}

export async function inviteCollaborator(projectId: string, email: string): Promise<InviteResult> {
  if (useMock) throw new Error(MOCK_TEAM_UNAVAILABLE);
  const out = await apiFetch<{ invite_url: string; account_exists: boolean; email_sent: boolean }>(
    `/api/v1/projects/${projectId}/members/invitations`,
    { method: 'POST', body: { email } },
  );
  return { inviteUrl: out.invite_url, accountExists: out.account_exists, emailSent: out.email_sent };
}

export async function revokeInvitation(projectId: string, invitationId: string): Promise<void> {
  if (useMock) throw new Error(MOCK_TEAM_UNAVAILABLE);
  await apiFetch<unknown>(`/api/v1/projects/${projectId}/members/invitations/${invitationId}`, {
    method: 'DELETE',
  });
}

/** Removing yourself is leaving the app. */
export async function removeCollaborator(projectId: string, accountId: string): Promise<void> {
  if (useMock) throw new Error(MOCK_TEAM_UNAVAILABLE);
  await apiFetch<unknown>(`/api/v1/projects/${projectId}/members/${accountId}`, {
    method: 'DELETE',
  });
}

export async function listMyInvitations(): Promise<MyInvitation[]> {
  if (useMock) return [];
  const { invitations } = await apiFetch<{ invitations: MyInvitationWire[] }>(
    '/api/v1/project-invites',
  );
  return invitations.map(toMyInvitation);
}

/** `key` is the token from an emailed link or an id from listMyInvitations. */
export async function getMyInvitation(key: string): Promise<MyInvitation> {
  if (useMock) throw new Error(MOCK_TEAM_UNAVAILABLE);
  const { invitation } = await apiFetch<{ invitation: MyInvitationWire }>(
    `/api/v1/project-invites/${encodeURIComponent(key)}`,
  );
  return toMyInvitation(invitation);
}

export async function acceptInvitation(key: string): Promise<{ projectSlug: string }> {
  if (useMock) throw new Error(MOCK_TEAM_UNAVAILABLE);
  const out = await apiFetch<{ project_slug: string }>(
    `/api/v1/project-invites/${encodeURIComponent(key)}/accept`,
    { method: 'POST' },
  );
  return { projectSlug: out.project_slug };
}

export async function declineInvitation(key: string): Promise<void> {
  if (useMock) throw new Error(MOCK_TEAM_UNAVAILABLE);
  await apiFetch<unknown>(`/api/v1/project-invites/${encodeURIComponent(key)}/decline`, {
    method: 'POST',
  });
}

/* ---------------------------------------------------------- upload links */

/**
 * Share-a-folder upload links (F25). Real-only: there is no mock counterpart,
 * because the whole point is a live URL somebody outside the product opens.
 *
 * The raw token comes back from `createUploadLink` and NOWHERE else: the
 * server stores only its hash. A screen that receives one must show it to the
 * owner immediately; it cannot be re-fetched.
 */
export interface UploadLink {
  id: string;
  label: string;
  prefix: string;
  status: 'active' | 'revoked' | 'expired' | 'completed';
  expiresAt: string;
  maxBytes: number;
  maxFiles: number;
  maxFileBytes: number;
  /** Space promised to uploads still in flight. Shown apart from what landed. */
  reservedBytes: number;
  uploadedBytes: number;
  uploadedFiles: number;
  lastUploadAt: string | null;
  createdAt: string;
}

interface UploadLinkWire {
  id: string;
  label: string;
  prefix: string;
  status: UploadLink['status'];
  expires_at: string;
  max_bytes: number;
  max_files: number;
  max_file_bytes: number;
  reserved_bytes: number;
  uploaded_bytes: number;
  uploaded_files: number;
  last_upload_at: string | null;
  created_at: string;
}

function toUploadLink(wire: UploadLinkWire): UploadLink {
  return {
    id: wire.id,
    label: wire.label,
    prefix: wire.prefix,
    status: wire.status,
    expiresAt: wire.expires_at,
    maxBytes: wire.max_bytes,
    maxFiles: wire.max_files,
    maxFileBytes: wire.max_file_bytes,
    reservedBytes: wire.reserved_bytes,
    uploadedBytes: wire.uploaded_bytes,
    uploadedFiles: wire.uploaded_files,
    lastUploadAt: wire.last_upload_at,
    createdAt: wire.created_at,
  };
}

export interface UploadLinkDefaults {
  maxBytes: number;
  maxFiles: number;
  expiresInHours: number;
}

export async function listUploadLinks(
  projectId: string,
): Promise<{ links: UploadLink[]; defaults: UploadLinkDefaults }> {
  const wire = await apiFetch<{
    links: UploadLinkWire[];
    defaults: { max_bytes: number; max_files: number; expires_in_hours: number };
  }>(`/api/v1/projects/${projectId}/upload-links`);
  return {
    links: wire.links.map(toUploadLink),
    defaults: {
      maxBytes: wire.defaults.max_bytes,
      maxFiles: wire.defaults.max_files,
      expiresInHours: wire.defaults.expires_in_hours,
    },
  };
}

export interface CreateUploadLinkInput {
  label: string;
  prefix: string;
  expiresInHours?: number;
  maxBytes?: number;
  allowedTypes?: string[];
}

/** The returned `url` is shown once and is not recoverable. */
export async function createUploadLink(
  projectId: string,
  input: CreateUploadLinkInput,
): Promise<UploadLink & { url: string }> {
  const wire = await apiFetch<UploadLinkWire & { url: string }>(
    `/api/v1/projects/${projectId}/upload-links`,
    {
      method: 'POST',
      body: {
        label: input.label,
        prefix: input.prefix,
        expires_in_hours: input.expiresInHours,
        max_bytes: input.maxBytes,
        allowed_types: input.allowedTypes,
      },
    },
  );
  return { ...toUploadLink(wire), url: wire.url };
}

export async function revokeUploadLink(projectId: string, linkId: string): Promise<UploadLink> {
  const wire = await apiFetch<UploadLinkWire>(
    `/api/v1/projects/${projectId}/upload-links/${linkId}`,
    { method: 'DELETE' },
  );
  return toUploadLink(wire);
}

/* --------------------------------------------------------------- deploys */

interface DeployWire {
  id: string;
  version: string;
  commit_sha: string | null;
  status: string;
  origin: string;
  promotion_tag: string | null;
  rollbackable: boolean;
  log_url: string | null;
  /** Why it did not go live, on failures recorded since 2026-09-24. */
  failure?: { source: 'workflow' | 'platform'; code: string | null; detail: string | null } | null;
  created_at: string;
}

/**
 * The recorded cause, as log text. cloud-api keeps no build log, so this is
 * the closest real thing, and BuildFailure's fallback fix request carries it
 * to the owner's coding agent verbatim.
 */
function failureLog(wire: DeployWire): string | undefined {
  const lines = [
    wire.failure
      ? `${wire.failure.source === 'workflow' ? 'GitHub Actions' : 'SV Cloud'}: ${wire.failure.code ?? 'unknown'}`
      : null,
    wire.failure?.detail ?? null,
    wire.log_url ? `Build log: ${wire.log_url}` : null,
  ].filter((line): line is string => line !== null);
  return lines.length > 0 ? lines.join('\n') : undefined;
}

function mapDeployStatus(status: string): DeployStatus {
  switch (status) {
    case 'building':
      return 'deploying';
    case 'live':
      return 'live';
    case 'failed':
      return 'didnt-work';
    case 'superseded':
    case 'rolled_back':
      return 'replaced';
    // GitHub cancelled the run because a newer push arrived. Not a failure.
    case 'cancelled':
      return 'skipped';
    default:
      return 'queued';
  }
}

function toDeploy(wire: DeployWire, projectId: string): Deploy {
  return {
    id: wire.id,
    projectId,
    status: mapDeployStatus(wire.status),
    commitSha: wire.commit_sha ?? '',
    // cloud-api's deploys table carries no commit message or author yet.
    // versionLabel is the closest real fact until that lands.
    commitMessage: wire.version,
    author: '',
    branch: 'main',
    createdAt: wire.created_at,
    finishedAt: wire.status === 'building' ? null : wire.created_at,
    durationMs: null,
    isCurrent: wire.status === 'live',
    rollbackable: wire.rollbackable,
    errorGroupId: null,
    trigger: wire.origin === 'rollback' ? 'rollback' : wire.origin === 'instant' ? 'manual' : 'push',
    buildId: wire.id,
    rawLog: wire.status === 'failed' ? failureLog(wire) : undefined,
  };
}

async function latestDeployId(projectId: string): Promise<string | null> {
  const { deploys } = await apiFetch<{ deploys: DeployWire[] }>(
    `/api/v1/projects/${projectId}/deploys`,
  );
  return deploys[0]?.id ?? null;
}

export async function listDeploys(projectId: string): Promise<Deploy[]> {
  if (useMock) return mock.listDeploys(projectId);
  const { deploys } = await apiFetch<{ deploys: DeployWire[] }>(
    `/api/v1/projects/${projectId}/deploys`,
  );
  return deploys.map((d) => toDeploy(d, projectId));
}

/**
 * No `GET /deploys/:id` exists: only the per-project list. This walks every
 * owned project's deploy list for a match, the same N+1 shape `listProjects`
 * already accepts; a dedicated route is the real fix (HANDOFF §2.4).
 */
export async function getDeploy(id: string): Promise<Deploy | null> {
  if (useMock) return mock.getDeploy(id);
  const { projects } = await apiFetch<{ projects: { id: string }[] }>('/api/v1/projects');
  for (const p of projects) {
    const { deploys } = await apiFetch<{ deploys: DeployWire[] }>(
      `/api/v1/projects/${p.id}/deploys`,
    );
    const match = deploys.find((d) => d.id === id);
    if (match) return toDeploy(match, p.id);
  }
  return null;
}

/* ---------------------------------------------------------------- secrets */

export async function listSecrets(projectId: string): Promise<Secret[]> {
  if (useMock) return mock.listSecrets(projectId);
  const { keys } = await apiFetch<{ keys: string[] }>(`/api/v1/projects/${projectId}/settings`);
  return keys.map((key) => ({
    id: key,
    projectId,
    key,
    // Write-only by design on both sides: cloud-api never returns a value.
    preview: '••••••••',
    reserved: false,
    createdAt: '',
    lastUsedAt: null,
  }));
}

/* --------------------------------------------------------------- traffic */

interface TrafficSpeedWire {
  median_ms: number | null;
  slowest_tenth_ms: number | null;
  by_day: (number | null)[];
}

interface TrafficWire {
  project_id: string;
  window: TrafficWindow;
  estimated: boolean;
  visits: number;
  previous_visits: number | null;
  series: { at: string; visits: number; errors: number }[];
  marks: { at: string; deployId: string; version?: string; failed?: boolean }[];
  outcome: Traffic['outcome'];
  speed: TrafficSpeedWire | null;
  paths: Traffic['paths'];
  places: Traffic['places'];
}

export async function getTraffic(projectId: string, window: TrafficWindow): Promise<Traffic | null> {
  if (useMock) return mock.getTraffic(projectId, window);
  let wire: TrafficWire;
  try {
    wire = await apiFetch<TrafficWire>(`/api/v1/projects/${projectId}/traffic?window=${window}`);
  } catch (err) {
    if (err instanceof ApiError && err.code === 'PROJECT_NOT_FOUND') return null;
    throw err;
  }
  return {
    projectId: wire.project_id,
    window: wire.window,
    estimated: wire.estimated,
    visits: wire.visits,
    previousVisits: wire.previous_visits,
    series: wire.series,
    // `version`/`failed` arrived 2026-09-27; a cached older payload lacks them.
    marks: wire.marks.map((m) => ({
      at: m.at,
      deployId: m.deployId,
      version: m.version ?? '',
      failed: m.failed ?? false,
    })),
    outcome: wire.outcome,
    // Null rule (HANDOFF §2.6.3): zero visits with a null speed is a quiet
    // app, not a family that hasn't shipped: collapse both into null here.
    speed:
      wire.visits === 0 || !wire.speed
        ? null
        : {
            medianMs: wire.speed.median_ms ?? 0,
            slowestTenthMs: wire.speed.slowest_tenth_ms ?? 0,
            byDay: wire.speed.by_day,
          },
    paths: wire.paths,
    places: wire.places,
  };
}

/* -------------------------------------------------------------- activity */

interface AuditEventWire {
  id: string;
  action: string;
  project_id: string | null;
  detail: Record<string, unknown> | null;
  created_at: string;
}

/** Reads one string field out of an audit event's loosely-typed `detail` blob, never throwing on a missing or non-string value. */
function field(detail: Record<string, unknown>, key: string): string {
  const value = detail[key];
  return typeof value === 'string' ? value : '';
}

/**
 * `action` -> {kind, title} for every string routes/*.ts's `recordAuditEvent`
 * calls actually use (verified against apps/cloud/src grep at wiring time).
 * `detail` fields read here are exactly what each call site writes - see the
 * corresponding route/service in apps/cloud for the source of truth.
 */
function describeAuditEvent(wire: AuditEventWire): { kind: ActivityKind; title: string } {
  const detail = wire.detail ?? {};
  switch (wire.action) {
    case 'database.row.insert':
      return { kind: 'database', title: t('Added a row to {table}', { table: field(detail, 'table') }) };
    case 'database.row.update':
      return { kind: 'database', title: t('Updated a row in {table}', { table: field(detail, 'table') }) };
    case 'database.row.delete':
      return { kind: 'database', title: t('Deleted a row from {table}', { table: field(detail, 'table') }) };
    case 'database.table.create':
      return { kind: 'database', title: t('Created a table: {table}', { table: field(detail, 'table') }) };
    case 'database.table.migrate':
      return {
        kind: 'database',
        title: t('Added a column to {table}: {column}', {
          table: field(detail, 'table'),
          column: field(detail, 'column'),
        }),
      };
    case 'settings.write':
      return { kind: 'keys', title: t('Set a key: {key}', { key: field(detail, 'key') }) };
    case 'settings.delete':
      return { kind: 'keys', title: t('Removed a key: {key}', { key: field(detail, 'key') }) };
    case 'deploy.rollback':
      return {
        kind: 'deploy',
        title: t('Rolled back to {version}', {
          version: field(detail, 'rolled_back_to_version'),
        }),
      };
    case 'project.mark_initialized':
      return { kind: 'deploy', title: t('Finished setting up') };
    case 'project.rename':
      return {
        kind: 'account',
        title: t('Renamed this project to {name}', { name: field(detail, 'to') }),
      };
    case 'project.pause':
      return { kind: 'account', title: t('Paused this project') };
    case 'project.resume':
      return { kind: 'account', title: t('Turned this project back on') };
    case 'project.page_cache_on':
      return { kind: 'account', title: t('Turned on page caching') };
    case 'project.page_cache_off':
      return { kind: 'account', title: t('Turned off page caching') };
    case 'project.connect':
      return { kind: 'account', title: t('Connected the repository') };
    case 'project.delete':
      return { kind: 'account', title: t('Deleted this project') };
    // Teams (routes/members.ts, routes/projectInvites.ts). `detail.email` is
    // the person the event is about, never the person who did it.
    case 'project.member.invited':
      return { kind: 'account', title: t('Invited {email}', { email: field(detail, 'email') }) };
    case 'project.member.joined':
      return { kind: 'account', title: t('{email} joined this project', { email: field(detail, 'email') }) };
    case 'project.member.removed':
      return { kind: 'account', title: t('Removed {email}', { email: field(detail, 'email') }) };
    case 'project.member.left':
      return { kind: 'account', title: t('{email} left this project', { email: field(detail, 'email') }) };
    case 'project.invitation.revoked':
      return {
        kind: 'account',
        title: t('Cancelled the invitation for {email}', { email: field(detail, 'email') }),
      };
    case 'project.invitation.declined':
      return {
        kind: 'account',
        title: t('{email} declined the invitation', { email: field(detail, 'email') }),
      };
    default:
      // A future action string this list doesn't know yet: still a real
      // event, so it stays visible rather than silently dropped, just
      // labelled with the raw action rather than a written sentence.
      return { kind: 'account', title: wire.action };
  }
}

function auditEventToActivity(wire: AuditEventWire): ActivityEvent {
  const { kind, title } = describeAuditEvent(wire);
  return { id: wire.id, projectId: wire.project_id ?? '', kind, title, detail: null, at: wire.created_at, actor: '' };
}

function deployToActivity(deploy: Deploy): ActivityEvent {
  return {
    id: `deploy:${deploy.id}`,
    projectId: deploy.projectId,
    kind: 'deploy',
    title: t('Deployed {version}', { version: deploy.commitMessage }),
    detail: null,
    at: deploy.createdAt,
    actor: '',
  };
}

/**
 * No single backend endpoint returns a project's whole activity feed: audit
 * events (GET /:id/activity, routes/activity.ts) cover secret/database/
 * account changes, and deploys (GET /:id/deploys, already real via
 * listDeploys above) are a separate table for a separate reason (every
 * push writes one, not just the ones a mutating dashboard action produced).
 * Merged and sorted here rather than asking the backend to interleave two
 * unrelated tables for one screen.
 */
export async function listActivity(projectId: string): Promise<ActivityEvent[]> {
  if (useMock) return mock.listActivity(projectId);
  const [auditRes, deploys] = await Promise.all([
    apiFetch<{ events: AuditEventWire[] }>(`/api/v1/projects/${projectId}/activity`),
    listDeploys(projectId),
  ]);
  const events = [
    ...auditRes.events.map(auditEventToActivity),
    ...deploys.map(deployToActivity),
  ];
  events.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  return events;
}

interface ForensicEventWire {
  event_id: string;
  trace_id: string;
  severity: 'info' | 'warn' | 'error';
  summary: string;
  status: number | null;
  route_pattern: string | null;
  script_version?: string | null;
  created_at: string;
  detail: { exceptions?: { name: string; message: string; stack?: string }[] } | null;
}

function findDeployForTimestamp(iso: string, deploys: Deploy[]): Deploy | undefined {
  if (deploys.length === 0) return undefined;
  const t = new Date(iso).getTime();
  const sorted = [...deploys].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  const match = sorted.find((d) => new Date(d.createdAt).getTime() <= t);
  return match ?? sorted[sorted.length - 1];
}

/**
 * GET /:id/obs/errors (routes/obs.ts) returns ungrouped forensic rows - one
 * per request, not the grouped-with-persisted-state ErrorGroup this
 * contract wants (see this file's header). Grouped here by route_pattern +
 * status + deployHash: the same key a person means by "my /api/checkout endpoint is
 * 500ing on this deployment", stable across exception messages that differ only by a dynamic
 * value. `state` is derived from whether the error occurred on the active deployment:
 * errors that only occurred before the current deploy are marked 'resolved'.
 */
export function groupErrors(
  rows: ForensicEventWire[],
  projectId: string,
  deploys: Deploy[] = [],
): ErrorGroup[] {
  const currentDeploy = deploys.find((d) => d.isCurrent || d.status === 'live') ?? deploys[0];
  const currentCreated = currentDeploy ? new Date(currentDeploy.createdAt).getTime() : 0;

  const groups = new Map<string, ErrorGroup>();
  for (const row of rows) {
    const deploy = findDeployForTimestamp(row.created_at, deploys);
    const deployHash = deploy?.commitSha || deploy?.commitMessage || row.script_version || undefined;
    const key = `${row.route_pattern ?? 'unknown'}:${row.status ?? 'none'}:${deployHash ?? 'none'}`;
    const exception = row.detail?.exceptions?.[0];
    const existing = groups.get(key);
    if (!existing) {
      const isPast =
        currentCreated > 0 &&
        new Date(row.created_at).getTime() < currentCreated &&
        deploy !== currentDeploy;
      groups.set(key, {
        id: key,
        projectId,
        deployHash,
        summary: row.summary,
        raw: exception ? `${exception.name}: ${exception.message}` : row.summary,
        location: row.route_pattern ?? t('unknown route'),
        count: 1,
        firstSeen: row.created_at,
        lastSeen: row.created_at,
        state: isPast ? 'resolved' : 'new',
      });
    } else {
      existing.count += 1;
      if (row.created_at < existing.firstSeen) existing.firstSeen = row.created_at;
      if (row.created_at > existing.lastSeen) existing.lastSeen = row.created_at;
      if (currentCreated > 0 && new Date(existing.lastSeen).getTime() >= currentCreated) {
        existing.state = 'new';
      }
    }
  }
  return [...groups.values()].sort(
    (a, b) => new Date(b.lastSeen).getTime() - new Date(a.lastSeen).getTime(),
  );
}

export async function listErrors(projectId: string): Promise<ErrorGroup[]> {
  if (useMock) return mock.listErrors(projectId);
  const [{ errors }, deploys] = await Promise.all([
    apiFetch<{ errors: ForensicEventWire[] }>(`/api/v1/projects/${projectId}/obs/errors`),
    listDeploys(projectId).catch(() => []),
  ]);
  return groupErrors(
    errors.filter((row) => row.severity === 'error'),
    projectId,
    deploys,
  );
}

/* -------------------------------------------------------------- database */

interface DbColumnWire {
  name: string;
  type: DbTable['columns'][number]['type'];
  nullable: boolean;
  primary_key: boolean;
}

interface DbTableWire {
  name: string;
  row_count: number;
  row_cap: number;
  columns: DbColumnWire[];
  updated_at: string | null;
  editable: boolean;
  not_editable_reason: DbTable['notEditableReason'];
}

function toDbTable(wire: DbTableWire, projectId: string): DbTable {
  return {
    name: wire.name,
    projectId,
    rowCount: wire.row_count,
    rowCap: wire.row_cap,
    columns: wire.columns.map((c) => ({
      name: c.name,
      type: c.type,
      nullable: c.nullable,
      primaryKey: c.primary_key,
    })),
    // cloud-api's introspection has no source for this, see database.ts.
    updatedAt: wire.updated_at ?? '',
    editable: wire.editable,
    notEditableReason: wire.not_editable_reason,
  };
}

export async function listTables(projectId: string): Promise<DbTable[]> {
  if (useMock) return mock.listTables(projectId);
  const { tables } = await apiFetch<{ tables: DbTableWire[] }>(
    `/api/v1/projects/${projectId}/database/tables`,
  );
  return tables.map((t) => toDbTable(t, projectId));
}

interface DbRowWire {
  id: string;
  cells: DbRow['cells'];
}

export async function listRows(projectId: string, tableName: string): Promise<DbRow[]> {
  return (await listRowsPage(projectId, tableName)).rows;
}

export interface RowPage {
  rows: DbRow[];
  /**
   * True when the table has more rows than the server will hand over in one
   * read (cloud-api's ROW_CAP, 200 at the time of writing).
   *
   * The grid has always been allowed to show a capped page. Export was not:
   * it was reading through the same capped call and writing the result to a
   * file called `<app>.sql`, so a table of 5,000 rows produced a 200-row file
   * with nothing anywhere saying so. A silently partial backup is worse than
   * no backup, which is why this flag is now carried out of the wire instead
   * of being dropped here.
   */
  truncated: boolean;
  /**
   * Pass this back as `after` to read the next page, in primary-key order.
   * Null on the last page, and for a table with no single-column primary key,
   * which cloud-api can only hand over as one capped page.
   */
  nextAfter: string | null;
}

/** The same read as `listRows`, keeping the wire's own truncation flag and its key for the next page. */
export async function listRowsPage(
  projectId: string,
  tableName: string,
  after?: string,
): Promise<RowPage> {
  if (useMock) {
    return { rows: await mock.listRows(projectId, tableName), truncated: false, nextAfter: null };
  }
  const query = after === undefined ? '' : `?after=${encodeURIComponent(after)}`;
  const { rows, truncated, next_after } = await apiFetch<{
    rows: DbRowWire[];
    truncated?: boolean;
    next_after?: string | null;
  }>(`/api/v1/projects/${projectId}/database/tables/${encodeURIComponent(tableName)}/rows${query}`);
  return {
    rows: rows.map((r) => ({ id: r.id, cells: r.cells })),
    truncated: truncated === true,
    nextAfter: next_after ?? null,
  };
}

/**
 * Single-row writes (F8). No mock counterpart: TableGrid only calls these
 * when `!useMock` (its `readOnly` gate), so there is nothing to re-export
 * from mock-api.ts the way the header's contract table describes for other
 * families. See TableGrid.tsx's own note on why editing is real-build-only.
 */
export async function insertRow(
  projectId: string,
  tableName: string,
  cells: DbRow['cells'],
): Promise<DbRow> {
  const { row } = await apiFetch<{ row: DbRowWire }>(
    `/api/v1/projects/${projectId}/database/tables/${encodeURIComponent(tableName)}/rows`,
    { method: 'POST', body: { cells } },
  );
  return { id: row.id, cells: row.cells };
}

export async function updateRow(
  projectId: string,
  tableName: string,
  rowId: string,
  cells: DbRow['cells'],
): Promise<DbRow> {
  const { row } = await apiFetch<{ row: DbRowWire }>(
    `/api/v1/projects/${projectId}/database/tables/${encodeURIComponent(tableName)}/rows/${encodeURIComponent(rowId)}`,
    { method: 'PATCH', body: { cells } },
  );
  return { id: row.id, cells: row.cells };
}

export async function deleteRow(projectId: string, tableName: string, rowId: string): Promise<void> {
  await apiFetch<void>(
    `/api/v1/projects/${projectId}/database/tables/${encodeURIComponent(tableName)}/rows/${encodeURIComponent(rowId)}`,
    { method: 'DELETE' },
  );
}

/* ----------------------------------------------------------------- files */

interface FileWire {
  path: string;
  name: string;
  bytes: number;
  content_type: string;
  source: FileObj['source'];
  updated_at: string | null;
  public_url: string | null;
}

export async function listFiles(projectId: string): Promise<FileObj[]> {
  if (useMock) return mock.listFiles(projectId);
  const { files } = await apiFetch<{ files: FileWire[] }>(`/api/v1/projects/${projectId}/files`);
  return files.map((f) => ({
    id: f.path,
    projectId,
    path: f.path,
    name: f.name,
    bytes: f.bytes,
    contentType: f.content_type,
    source: f.source,
    updatedAt: f.updated_at ?? '',
    publicUrl: f.public_url,
  }));
}

/* ---------------------------------------------------------------- counts */

export interface ProjectCounts {
  /** Tables in the app's database, or null when the platform could not read it. */
  tables: number | null;
  /** Objects in the app's file storage, or null when it could not be read. */
  files: number | null;
  /** True when there are more files than the count walked, so it is a floor. */
  filesTruncated: boolean;
  /** When the numbers were taken. Shown on the panel, never hidden. */
  computedAt: string;
  /** True when this came from the cache rather than being measured just now. */
  cached: boolean;
}

interface ProjectCountsWire {
  tables: number | null;
  files: number | null;
  files_truncated: boolean;
  computed_at: string;
  cached: boolean;
}

/**
 * The overview's two expensive facts, in one cached read.
 *
 * The overview used to get these by calling `listTables` and `listFiles` and
 * taking `.length`: a full column introspection plus a `count(*)` per table,
 * and a whole page of file records, to render "48 tables" and "200 files". The
 * server counts them properly and caches for a day now (apps/cloud's
 * services/projectCounts.ts), so the page view costs one cheap read and the
 * numbers carry the time they were taken.
 *
 * `refresh` recomputes, and is the panel's button only. Never pass it on a
 * page view: that would reinstate exactly the cost this replaced.
 */
export async function getProjectCounts(
  projectId: string,
  options: { refresh?: boolean } = {},
): Promise<ProjectCounts> {
  if (useMock) {
    const [tables, files] = await Promise.all([
      mock.listTables(projectId),
      mock.listFiles(projectId),
    ]);
    return {
      tables: tables.length,
      files: files.length,
      filesTruncated: false,
      computedAt: new Date().toISOString(),
      cached: !options.refresh,
    };
  }
  const wire = await apiFetch<ProjectCountsWire>(
    `/api/v1/projects/${projectId}/counts${options.refresh ? '?refresh=1' : ''}`,
  );
  return {
    tables: wire.tables,
    files: wire.files,
    filesTruncated: wire.files_truncated,
    computedAt: wire.computed_at,
    cached: wire.cached,
  };
}

/* ---------------------------------------------------- GitHub installations */

export interface Installation {
  id: string;
  githubAccountLogin: string;
  createdAt: string;
}

interface InstallationWire {
  id: string;
  github_account_login: string;
  created_at: string;
}

/** Real-only: the connect flow branches on VITE_API_MODE before ever calling this. */
export async function listInstallations(): Promise<Installation[]> {
  if (useMock) throw new Error('listInstallations has no mock implementation, the connect flow reads lib/mock/onboarding directly in mock mode.');
  const { installations } = await apiFetch<{ installations: InstallationWire[] }>(
    '/api/v1/installations',
  );
  return installations.map((i) => ({
    id: i.id,
    githubAccountLogin: i.github_account_login,
    createdAt: i.created_at,
  }));
}

/** The URL to send the browser to start (or re-confirm) a GitHub App install. */
export async function getInstallConnectUrl(): Promise<string> {
  const { url } = await apiFetch<{ url: string }>('/api/v1/installations/connect-url');
  return url;
}

interface InstallationContextWire {
  account_login: string;
  repos: { name: string; full_name: string; empty: boolean; pushed_at: string | null }[];
}

export interface InstallationContext {
  accountLogin: string;
  repos: RepoCandidate[];
}

export async function getInstallation(installationId: string): Promise<InstallationContext> {
  const wire = await apiFetch<InstallationContextWire>(`/api/v1/installations/${installationId}`);
  return {
    accountLogin: wire.account_login,
    repos: wire.repos.map((r) => {
      const [owner, name] = r.full_name.split('/');
      return {
        id: r.full_name,
        owner: owner ?? wire.account_login,
        name: name ?? r.name,
        // Not exposed by GET /installations/:id, the connect flow only
        // learns the real default branch once GET /projects/:id/runs/latest
        // reports repo.init's outcome, or from repository_wire once a
        // project exists. 'main' is the same assumption ConnectCode's
        // Advanced branch picker already defaults to.
        defaultBranch: 'main',
        isEmpty: r.empty,
        pushedAt: r.pushed_at ?? '',
      };
    }),
  };
}

/* --------------------------------------------------------- name/subdomain */

export type SubdomainVerdict =
  | { available: true }
  | { available: false; reason: 'invalid' | 'reserved' | 'taken' };

export async function checkSubdomain(slug: string): Promise<SubdomainVerdict> {
  return apiFetch<SubdomainVerdict>(`/api/v1/projects/subdomain?slug=${encodeURIComponent(slug)}`);
}

/* ------------------------------------------------------------ project connect */

export interface ConnectProjectInput {
  name: string;
  /** Where the code lives. Omitted means GitHub, which then needs the two fields below. */
  codeHost?: 'github' | 'svgit';
  repoFullName?: string;
  githubInstallationId?: string;
  requestedSubdomain: string;
}

export interface ConnectProjectResult {
  kind: 'accepted' | 'existing';
  projectId: string;
  /** Only set for `kind: 'accepted'`, poll getRun(runId) for progress. */
  runId: string | null;
  slug: string;
}

interface ProvisionAcceptedWire {
  project_id: string;
  run_id: string;
}

interface ProjectDetailWire {
  id: string;
  slug: string;
}

/**
 * POST /projects/connect answers one of two shapes on the SAME 2xx family:
 * a 202 run envelope for a fresh provision, or a 200 projectDetail when the
 * (account, repo) pair already has an active project (idempotent replay,
 * services/connect.ts's prepareConnect). `apiFetch` doesn't hand back the
 * HTTP status, so the two are told apart by shape: a run envelope has
 * `run_id`, a projectDetail doesn't.
 */
export async function connectProject(input: ConnectProjectInput): Promise<ConnectProjectResult> {
  const wire = await apiFetch<ProvisionAcceptedWire | ProjectDetailWire>('/api/v1/projects/connect', {
    method: 'POST',
    body: {
      name: input.name,
      // An SV Git project has no GitHub repository, and the server refuses a
      // request that names one, so the two fields are left out entirely.
      ...(input.codeHost === 'svgit'
        ? { code_host: 'svgit' }
        : {
            repo_full_name: input.repoFullName,
            github_installation_id: Number(input.githubInstallationId),
          }),
      requested_subdomain: input.requestedSubdomain,
      // FRAMEWORK_TYPES is exactly one value today (db/schema.ts), there is
      // no framework choice on the onboarding screens to read this from.
      framework_type: 'hono',
      scheduled_cleanup_at: null,
    },
  });
  if ('run_id' in wire) {
    return { kind: 'accepted', projectId: wire.project_id, runId: wire.run_id, slug: input.requestedSubdomain };
  }
  return { kind: 'existing', projectId: wire.id, runId: null, slug: wire.slug };
}

/* ------------------------------------------------------------------- runs */

export type RunStepStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped';

export interface RunStep {
  key: string;
  label: string;
  status: RunStepStatus;
  detail: string | null;
}

export interface RunError {
  code: string;
  message: string;
}

export interface Run {
  id: string;
  projectId: string;
  kind: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  error: RunError | null;
  steps: RunStep[];
}

interface RunWire {
  id: string;
  project_id: string;
  kind: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  error: RunError | null;
  steps: { key: string; label: string; status: RunStepStatus; detail: string | null }[];
}

export async function getRun(runId: string): Promise<Run> {
  const wire = await apiFetch<RunWire>(`/api/v1/runs/${runId}`);
  return {
    id: wire.id,
    projectId: wire.project_id,
    kind: wire.kind,
    status: wire.status,
    error: wire.error,
    steps: wire.steps,
  };
}

/* ------------------------------------------------------------ settings writes */

export async function setSecret(projectId: string, key: string, value: string): Promise<void> {
  await apiFetch<void>(`/api/v1/projects/${projectId}/settings/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: { value },
  });
}

export async function deleteSecret(projectId: string, key: string): Promise<void> {
  await apiFetch<void>(`/api/v1/projects/${projectId}/settings/${encodeURIComponent(key)}`, {
    method: 'DELETE',
  });
}

/* -------------------------------------------------------------- deployment */

export interface DeleteProjectResult {
  runId: string;
  otherActiveProjects: boolean;
}

export async function deleteProject(projectId: string): Promise<DeleteProjectResult> {
  const wire = await apiFetch<ProvisionAcceptedWire & { other_active_projects: boolean }>(
    `/api/v1/projects/${projectId}`,
    { method: 'DELETE' },
  );
  return { runId: wire.run_id, otherActiveProjects: wire.other_active_projects };
}

export async function rollbackDeploy(projectId: string, deployId: string): Promise<void> {
  await apiFetch<void>(`/api/v1/projects/${projectId}/deploys/${deployId}/rollback`, {
    method: 'POST',
  });
}

/* --------------------------------------------------------------- staging */

/**
 * An app's staging environment, and the switch that decides where a push goes.
 *
 * Every rule lives on the server (apps/cloud's services/staging.ts). What this
 * layer owes is the shape, and what the screens owe is the consequence said out
 * loud: turning staging on is the moment pushes stop reaching the live app.
 */
export interface StagingReadiness {
  stagingReady: boolean;
  workflowOk: boolean;
  workflowProblem: string | null;
  missingSettingNames: string[];
  deployMode: DeployMode;
}

const MOCK_STAGING_UNAVAILABLE = 'Staging environments are not part of the demo build.';

interface StagingPreflightWire {
  staging_ready: boolean;
  workflow_ok: boolean;
  workflow_problem: string | null;
  missing_secret_names: string[];
  deploy_mode: DeployMode;
}

/**
 * The demo build DOES rehearse a staging environment, on one seeded project.
 * Two environments that must never be confused is precisely the thing design
 * review has to be able to look at, and an earlier version of this file refused
 * to fake any of it, which left the one screen that most needs reviewing
 * unreviewable.
 *
 * What stays unfaked is the three MUTATIONS below. Creating a staging
 * environment provisions a second app and a second database; there is no
 * meaningful demo of that, and a button that pretended would be the lying
 * control this product keeps removing. So the demo ships one project that
 * already has staging switched on, and the buttons that would change that say
 * plainly they are not part of it.
 */
export async function getStagingReadiness(projectId: string): Promise<StagingReadiness> {
  if (useMock) return mock.getStagingReadiness(projectId);
  const wire = await apiFetch<StagingPreflightWire>(
    `/api/v1/projects/${projectId}/staging/preflight`,
  );
  return {
    stagingReady: wire.staging_ready,
    workflowOk: wire.workflow_ok,
    workflowProblem: wire.workflow_problem,
    missingSettingNames: wire.missing_secret_names,
    deployMode: wire.deploy_mode,
  };
}

export async function createStagingEnvironment(projectId: string): Promise<void> {
  if (useMock) throw new Error(MOCK_STAGING_UNAVAILABLE);
  await apiFetch<unknown>(`/api/v1/projects/${projectId}/staging`, { method: 'POST' });
}

export async function enableStaging(projectId: string, force = false): Promise<void> {
  if (useMock) throw new Error(MOCK_STAGING_UNAVAILABLE);
  await apiFetch<unknown>(
    `/api/v1/projects/${projectId}/staging/enable${force ? '?force=1' : ''}`,
    { method: 'POST' },
  );
}

export async function disableStaging(projectId: string): Promise<void> {
  if (useMock) throw new Error(MOCK_STAGING_UNAVAILABLE);
  await apiFetch<unknown>(`/api/v1/projects/${projectId}/staging/disable`, { method: 'POST' });
}

/* ------------------------------------------------------------------ usage */

interface UsageMetricWire {
  resource: UsageMetric['resource'];
  label: string;
  used: number | null;
  limit: number | null;
  unit: UsageMetric['unit'];
  state: UsageMetric['state'];
  measured_at: string | null;
  approximate: boolean;
  breakdown?: {
    projects: Array<{ project_id: string; name: string | null; used: number }>;
    others: { project_count: number; used: number } | null;
  };
}

interface UsageWire {
  project_id: string | null;
  project_count?: number;
  plan: Usage['plan'];
  plan_label: string;
  plan_expires_at: string | null;
  credits?: { paid_usd: string; promotional_usd: string; promotional_next_expiry: string | null };
  overage?: {
    units: number;
    rate_usd_per_million: string | null;
    estimated_usd: string;
    charged_usd: string;
    uncovered_usd: string;
    paid_as_used?: boolean;
  };
  credits_spent?: { total_usd: string; overage_usd: string; plan_usd: string; available_usd: string };
  billing?: {
    standing: 'trial' | 'active' | 'lapsed';
    plan_id: Usage['plan'];
    plan_label: string;
    plan_ends_at: string | null;
    credits_pay_plan: boolean;
    pending_plan_id: Usage['plan'] | null;
    renewal_usd: string | null;
    renewal_covered: boolean;
    lapse_enforced: boolean;
  };
  period: 'month';
  period_start: string;
  resets_at: string;
  over_limit: boolean;
  metrics: UsageMetricWire[];
  advanced: {
    requests: number;
    cpu_ms: number;
    stateful_ms: number;
    database_rows: number | null;
    counter_basis: string;
    counted_from: string | null;
  };
}

export async function getUsage(projectId: string | null): Promise<Usage | null> {
  if (useMock) return mock.getUsage(projectId);
  const path = projectId ? `/api/v1/projects/${projectId}/usage` : '/api/v1/usage';
  let wire: UsageWire;
  try {
    wire = await apiFetch<UsageWire>(path);
  } catch (err) {
    if (err instanceof ApiError && err.code === 'PROJECT_NOT_FOUND') return null;
    throw err;
  }
  return {
    projectId: wire.project_id,
    plan: wire.plan,
    planLabel: wire.plan_label,
    planExpiresAt: wire.plan_expires_at,
    credits: wire.credits
      ? {
          paidUsd: wire.credits.paid_usd,
          promotionalUsd: wire.credits.promotional_usd,
          promotionalNextExpiry: wire.credits.promotional_next_expiry,
        }
      : undefined,
    overage: wire.overage
      ? {
          units: wire.overage.units,
          rateUsdPerMillion: wire.overage.rate_usd_per_million,
          estimatedUsd: wire.overage.estimated_usd,
          // Older cloud-api builds have no charged_usd: nothing paid yet.
          chargedUsd: wire.overage.charged_usd ?? '0.00',
          uncoveredUsd: wire.overage.uncovered_usd,
          paidAsUsed: wire.overage.paid_as_used ?? false,
        }
      : undefined,
    creditsSpent: wire.credits_spent
      ? {
          totalUsd: wire.credits_spent.total_usd,
          overageUsd: wire.credits_spent.overage_usd,
          planUsd: wire.credits_spent.plan_usd,
          availableUsd: wire.credits_spent.available_usd,
        }
      : undefined,
    billing: wire.billing
      ? {
          standing: wire.billing.standing,
          planLabel: wire.billing.plan_label,
          planEndsAt: wire.billing.plan_ends_at,
          creditsPayPlan: wire.billing.credits_pay_plan,
          pendingPlanId: wire.billing.pending_plan_id,
          renewalUsd: wire.billing.renewal_usd,
          renewalCovered: wire.billing.renewal_covered,
          lapseEnforced: wire.billing.lapse_enforced,
        }
      : undefined,
    resetsAt: wire.resets_at,
    // Metrics with limits (compute, database, files) are always included;
    // an unmeasured or empty usage defaults to 0 ('none' / 'No traffic yet')
    // rather than being dropped from the screen. `state` is passed
    // straight through, never recomputed: one threshold, computed once, on
    // the server (services/usage/usage.ts's header).
    metrics: wire.metrics
      .filter((m): m is UsageMetricWire & { limit: number } => m.limit !== null)
      .map((m) => ({
        resource: m.resource,
        label: m.label,
        used: m.used ?? 0,
        limit: m.limit,
        unit: m.unit,
        state: m.state,
        breakdown: m.breakdown
          ? {
              projects: m.breakdown.projects.map((p) => ({
                projectId: p.project_id,
                name: p.name,
                used: p.used,
              })),
              others: m.breakdown.others
                ? {
                    projectCount: m.breakdown.others.project_count,
                    used: m.breakdown.others.used,
                  }
                : null,
            }
          : undefined,
      })),
    advanced: {
      requests: wire.advanced.requests,
      cpuMs: wire.advanced.cpu_ms,
      statefulMs: wire.advanced.stateful_ms ?? 0,
      databaseRows: wire.advanced.database_rows,
    },
  };
}

/* ----------------------------------------------------------------- bundle */

/**
 * The bundle version an app is running, and what taking a newer one would
 * involve (apps/cloud F27). Real-only, like the upload links above: the demo
 * apparatus has nothing to fake against, and the screen carries its own
 * fixtures for design review instead (screens/project/Bundle.tsx).
 *
 * The wire's own field names are kept on the plan's files rather than
 * renamed to camel case, because the same objects are what a coding agent
 * reads through the platform's tools, and two spellings of one thing is how
 * a screen and an agent start describing different files to the same person.
 */
export interface BundleStatus {
  projectId: string;
  /** The version the repo itself records, or null when it records none. */
  currentVersion: string | null;
  latestVersion: string;
  updateAvailable: boolean;
  /** No recorded starting point, so the first update has nothing to compare against. */
  legacy: boolean;
  baseUnavailableReason: string | null;
  workflowStale: boolean;
  workflowWarning: string | null;
}

export type BundleAction =
  | 'skip'
  | 'fast-forward'
  | 'conflict'
  | 'create'
  | 'retire'
  | 'retire-conflict'
  | 'locate'
  | 'merge-manifest'
  | 'report-only';

export interface BundleFile {
  src: string;
  path: string;
  class: string;
  action: BundleAction;
  guidance: string;
  retirement?: { src: string; replaced_by?: string | null; note: string };
}

export interface BundlePlan extends BundleStatus {
  framework: string;
  migrateOnce: boolean;
  files: BundleFile[];
  migrationNotes: string | null;
  instructions: string;
}

interface BundleStatusWire {
  project_id: string;
  current_version: string | null;
  latest_version: string;
  update_available: boolean;
  legacy: boolean;
  base_unavailable_reason: string | null;
  workflow_stale: boolean;
  workflow_warning: string | null;
}

interface BundlePlanWire extends BundleStatusWire {
  framework: string;
  migrate_once: boolean;
  files: BundleFile[];
  migration_notes: string | null;
  instructions: string;
}

function toBundleStatus(wire: BundleStatusWire): BundleStatus {
  return {
    projectId: wire.project_id,
    currentVersion: wire.current_version,
    latestVersion: wire.latest_version,
    updateAvailable: wire.update_available,
    legacy: wire.legacy,
    baseUnavailableReason: wire.base_unavailable_reason,
    workflowStale: wire.workflow_stale,
    workflowWarning: wire.workflow_warning,
  };
}

export async function getBundleStatus(projectId: string): Promise<BundleStatus> {
  return toBundleStatus(
    await apiFetch<BundleStatusWire>(`/api/v1/projects/${projectId}/bundle`),
  );
}

/**
 * The full per-file plan. Costs a handful of GitHub reads server-side, so it
 * is a second call rather than part of the status: the notice on the overview
 * needs only "is there an update", and paying for the whole plan to draw one
 * sentence would be a tax on every page load.
 */
export async function getBundlePlan(projectId: string): Promise<BundlePlan> {
  const wire = await apiFetch<BundlePlanWire>(`/api/v1/projects/${projectId}/bundle/plan`);
  return {
    ...toBundleStatus(wire),
    framework: wire.framework,
    migrateOnce: wire.migrate_once,
    files: wire.files,
    migrationNotes: wire.migration_notes,
    instructions: wire.instructions,
  };
}
